import React, { useCallback } from 'react'
import styled from 'styled-components'
import { SwipeableListItem } from '../../../../../generic/swipe/SwipeableListAdapter'
import TitleRow from '../../kosherTeudot/generic/TitleRow'
import TitleRowItem from '../../kosherTeudot/generic/TitleRowItem'
import InvoiceIcon from '../../../../../generic/icons/IconLib/InvoiceIcon'
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon'
import CalendatDay from '../../../../../generic/icons/IconLib/CalendarIcon'
import PrintIcon from '../../../../../generic/icons/IconLib/PrintIcon'
import { IQuotationTitle } from '../types/quotation'
import { convertToDate } from '../../../../../modules/convertDateString'

type Props = {
  item: IQuotationTitle
  onStatusChange?: (titleID: number, status: number) => void
  onClick?: (recId: number) => void
  onPrint?: (recId: number, customerId: number) => void
  handleDeleteQuotation?: (titleID: number) => void
}

const STATUS_OPTIONS = [
  { value: 1, label: 'פתוח' },
  { value: 2, label: 'מאושר' },
  { value: 3, label: 'נדחה' },
]

const CardContent: React.FC<{
  title: string
  customerDesc: string
  date: string
  currentStatusValue: number
  onStatusChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void
  printPdf?: () => void
}> = ({ title, customerDesc, date, currentStatusValue, onStatusChange, printPdf }) => (
  
  <CardInner>
    
    <SideSelectWrapper
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      
      {onStatusChange && (  
        <StatusSelect
          value={currentStatusValue}
          onChange={onStatusChange}
          aria-label="שינוי סטטוס"
          tabIndex={0}
          $statusValue={currentStatusValue}
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </StatusSelect>
      )}
      {printPdf && (
        <SidePrintWrapper
          onClick={printPdf}
        >
            <PrintIcon />
        </SidePrintWrapper>
      )}
    </SideSelectWrapper>

    <CardBody>
      <TitleRow>
        <TitleRowItem name={title} icon={<InvoiceIcon />} />
      </TitleRow>
      <TitleRow>
        <TitleRowItem name={customerDesc?.trim()} icon={<UserIcon />} />
      </TitleRow>
      <TitleRow>
        <TitleRowItem name={convertToDate(date)} icon={<CalendatDay />} />
      </TitleRow>
    </CardBody>
  </CardInner>
)

const QuotationTitleCard: React.FC<Props> = ({ item, onStatusChange, onClick, onPrint, handleDeleteQuotation }) => {
  const { recId, title, customerDesc, customerId, date, status } = item

  const handlePrintPdf = useCallback(() => {
    onPrint?.(recId, customerId)
  }, [onPrint, recId, customerId])

  const handleStatusChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation()
    if (onStatusChange) {
      onStatusChange(recId, Number(e.target.value))
    }
  }, [onStatusChange, recId])

  const contentProps = {
    title,
    customerDesc,
    date,
    currentStatusValue: status,
    onStatusChange: onStatusChange ? handleStatusChange : undefined,
    printPdf: onPrint ? handlePrintPdf : undefined,
    handleDeleteQuotation: handleDeleteQuotation ? handleDeleteQuotation : undefined
  }

  if (!onClick) {
    return (
      <CardWrapper>
        <CardContent {...contentProps} />
      </CardWrapper>
    )
  }

  const handleSwipeAction = () => {
    onClick(recId)
  }

  const handleDelete = () => {
    if (handleDeleteQuotation) {
      handleDeleteQuotation(recId)
    }
  }

  return (
    <CardWrapper>
      <SwipeableListItem
        swipeRight={{
          content: <SwipeContent>עדכון</SwipeContent>,
          action: handleSwipeAction,
        }}
        swipeLeft={{
          content: <SwipeContent $isDelete>מחיקה</SwipeContent>,
          action: handleDelete,
        }}
        threshold={0.3} 
      >
        <CardContent {...contentProps} />
      </SwipeableListItem>
    </CardWrapper>
  )
}

export default QuotationTitleCard

const CardWrapper = styled.div`
  position: relative;
  width: 100%;
  border-radius: 20px;
  background: #fff;
  -webkit-filter: drop-shadow(4px 3px 3px rgba(41, 127, 118, 0.51));
  filter: drop-shadow(4px 3px 3px rgba(41, 127, 118, 0.51));
  cursor: grab;

  .swipeable-list-item {
    border-radius: 20px;
  }
`

const CardInner = styled.div`
  position: relative;
  width: 100%;
  padding: 0.5em 1em;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  background: #fff;
  border-radius: 20px;
`

const SwipeContent = styled.div<{ $isDelete?: boolean }>`
  background: ${(props) => (props.$isDelete ? 'tomato' : '#7ab0ab')};
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$isDelete ? 'flex-end' : 'flex-start')};
  color: #fff;
  padding: 0 1em;
  font-weight: 600;
  border-radius: 20px;
`

const SideSelectWrapper = styled.div`
  position: absolute;
  left: 1em;
  top: calc(50%);
  transform: translateY(-50%);
  display: flex;
  gap: 0.5em;
  align-items: center;
  svg {
    color: #297F76;
    cursor: pointer;
  }
`
const SidePrintWrapper = styled.div`

`
const StatusSelect = styled.select<{ $statusValue: number }>`
  appearance: none;
  -webkit-appearance: none;
  border: none;
  outline: none;
  background: ${(props) => (props.$statusValue === 1 ? '#f2f2f2' : props.$statusValue === 2 ? '#e6f7f1' : '#fdecea')};
  color: ${(props) => (props.$statusValue === 1 ? '#808080' : props.$statusValue === 2 ? '#297f76' : '#d32f2f')};
  font-weight: 600;
  font-size: 0.85em;
  padding: 0.3em 0.8em;
  border-radius: 20px;
  cursor: pointer;
  text-align: center;
  min-width: 4em;
`

const CardBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  padding-left: 4em;
`
